/**
 * ConnectivitySnapshot — answers "what kind of connectivity did I actually get?"
 *
 * Renders the operator / RAT / signal / IP that the modem ended up on. Pulls
 * from `metrics.cellular.*` first (populated by `cellular_attach_verify`
 * primitive in the system quick-test flows), then falls back to the legacy
 * `registeredNetwork*` enrichment fields written by
 * `PrimitiveCallExecutor.enrichParentExecution`. Returns null when no
 * connectivity data is available so older runs don't render an empty card.
 */
import React from 'react';
import { Signal, Globe, Wifi, MapPin, AlertTriangle, CheckCircle2, Radio, Cpu, Gauge, Timer } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/Tooltip';
import { cn } from '@/lib/utils';
import type { ExecutionMetricsMap } from '@simcheck/shared-types';

export interface ConnectivitySnapshotProps {
  metrics?: ExecutionMetricsMap;
  bearerClass?: string;
  // Legacy enrichment fields written ad-hoc on the execution doc.
  registeredNetwork?: string;
  registeredNetworkName?: string;
  registeredNetworkType?: string | number;
  registeredNetworkMcc?: string;
  registeredNetworkMnc?: string;
  // Location context — populated by FlowExecutor pre-flow enrichment so
  // customers see "where did this run happen?" right next to "what did
  // we attach to?".
  locationName?: string;
  countryCode?: string;
  countryName?: string;
}

interface NormalizedSnapshot {
  operatorName?: string;
  operatorMcc?: string;
  operatorMnc?: string;
  /** Already translated to a human label (e.g. "LTE", "NB-IoT") — never a raw code. */
  technology?: string;
  /** Raw QNWINFO access-tech string when available (e.g. "FDD LTE", "NR5G-NSA"). */
  accessTechName?: string;
  bearerClass?: string;
  registrationState?: number;
  signalRsrp?: number;
  signalRsrq?: number;
  signalSinr?: number;
  signalRssi?: number;
  ip?: string;
  ipFamily?: string;
  apn?: string;
  /** Live serving-cell band (from QNWINFO). */
  bandLive?: string;
  bandChannel?: number;
  dlBandwidth?: string;
  ulBandwidth?: string;
  pci?: number;
  cellId?: string;
  /** Modem hardware capability — Cat 1/4/12/19/... */
  lteCategoryMax?: number;
  /** Modem hardware supported bands. */
  lteFddBands?: number[];
  lteTddBands?: number[];
  nrSaBands?: number[];
  nrNsaBands?: number[];
  requestedMcc?: string;
  requestedMnc?: string;
  requestedRat?: string;
  requestedHonored?: boolean;
  locationName?: string;
  countryName?: string;
  // Data-test KPIs surfaced as headline tiles when present.
  latencyAvgMs?: number;
  latencyMinMs?: number;
  latencyMaxMs?: number;
  packetLossPct?: number;
  throughputKbps?: number;
  dnsMs?: number;
  // NSA / EN-DC enrichment (Phase 3 of 5G NSA support).
  nsaState?: 'nsa_aggregating' | 'nsa_available' | 'endc_capable_no_attach' | 'lte_only' | 'unknown';
  endcRestrict?: number;
  fiveGIcon?: number;
  nrBand?: number;
  nrArfcn?: number;
  nrPci?: number;
  nrRsrp?: number;
  nrRsrq?: number;
  nrSinr?: number;
}

function normalize(props: ConnectivitySnapshotProps): NormalizedSnapshot {
  const c = props.metrics?.cellular ?? {};
  const d = props.metrics?.data ?? {};
  // Translate technology aggressively: prefer cellular metrics, fall back to
  // legacy registeredNetworkType. Both may arrive as numbers (3GPP AcT codes)
  // — formatTechnology hides anything we can't map.
  const tech = formatTechnology(c.technology ?? props.registeredNetworkType);

  // The legacy `registeredNetwork` field stores whatever COPS returned —
  // alpha-long ("Partner IL"), alpha-short, OR numeric PLMN ("42501") if
  // the modem's COPS mode was set to numeric. We only treat it as an
  // alpha operator name if it's NOT all digits; otherwise it's a PLMN
  // we extract MCC/MNC from below so the headline stays "operator NAME"
  // and the sub-line stays "operator NUMBER" — not the reverse.
  const rawLegacy = props.registeredNetwork;
  const legacyIsAlphaName =
    typeof rawLegacy === 'string' && rawLegacy.length > 0 && !/^\d{5,6}$/.test(rawLegacy);
  let derivedMccFromLegacy: string | undefined;
  let derivedMncFromLegacy: string | undefined;
  if (typeof rawLegacy === 'string' && /^\d{5,6}$/.test(rawLegacy)) {
    derivedMccFromLegacy = rawLegacy.substring(0, 3);
    derivedMncFromLegacy = rawLegacy.substring(3);
  }

  return {
    operatorName:
      c.operatorName ??
      props.registeredNetworkName ??
      (legacyIsAlphaName ? rawLegacy : undefined),
    operatorMcc: c.operatorMcc ?? props.registeredNetworkMcc ?? derivedMccFromLegacy,
    operatorMnc: c.operatorMnc ?? props.registeredNetworkMnc ?? derivedMncFromLegacy,
    technology: tech ?? undefined,
    accessTechName: c.accessTechName,
    bearerClass: c.bearerClass ?? props.bearerClass,
    registrationState: c.registrationState,
    signalRsrp: c.signalRsrp,
    signalRsrq: c.signalRsrq,
    signalSinr: c.signalSinr,
    signalRssi: c.signalRssi,
    ip: c.ip,
    ipFamily: c.ipFamily,
    apn: c.apn,
    bandLive: c.bandLive,
    bandChannel: c.bandChannel,
    dlBandwidth: c.dlBandwidth,
    ulBandwidth: c.ulBandwidth,
    pci: c.pci,
    cellId: c.cellId,
    lteCategoryMax: c.lteCategoryMax,
    lteFddBands: c.lteFddBands,
    lteTddBands: c.lteTddBands,
    nrSaBands: c.nrSaBands,
    nrNsaBands: c.nrNsaBands,
    requestedMcc: c.requestedMcc,
    requestedMnc: c.requestedMnc,
    requestedRat: c.requestedRat,
    requestedHonored: c.requestedHonored,
    locationName: props.locationName,
    countryName: props.countryName ?? props.countryCode,
    latencyAvgMs: d.rttAvgMs,
    latencyMinMs: d.rttMinMs,
    latencyMaxMs: d.rttMaxMs,
    packetLossPct: d.packetLossPct,
    throughputKbps: d.throughputKbps,
    dnsMs: d.dnsMs,
    nsaState: (c as any).nsaState,
    endcRestrict: (c as any).endcRestrict,
    fiveGIcon: (c as any).fiveGIcon,
    nrBand: (c as any).nrBand,
    nrArfcn: (c as any).nrArfcn,
    nrPci: (c as any).nrPci,
    nrRsrp: (c as any).nrRsrp,
    nrRsrq: (c as any).nrRsrq,
    nrSinr: (c as any).nrSinr,
  };
}

function hasAnyData(s: NormalizedSnapshot): boolean {
  return !!(
    s.operatorName ||
    s.operatorMcc ||
    s.technology ||
    s.accessTechName ||
    s.bearerClass ||
    s.signalRsrp !== undefined ||
    s.signalRssi !== undefined ||
    s.ip ||
    s.locationName ||
    s.countryName ||
    s.bandLive ||
    s.dlBandwidth ||
    s.lteCategoryMax ||
    (s.lteFddBands && s.lteFddBands.length > 0) ||
    (s.lteTddBands && s.lteTddBands.length > 0) ||
    (s.nrSaBands && s.nrSaBands.length > 0) ||
    s.latencyAvgMs !== undefined ||
    s.throughputKbps !== undefined
  );
}

/** Format kbps as "5.2 Mbps" once over 1 Mbps, otherwise "523 kbps". */
function formatThroughput(kbps?: number): string | null {
  if (typeof kbps !== 'number' || !Number.isFinite(kbps)) return null;
  if (kbps >= 1000) return `${(kbps / 1000).toFixed(1)} Mbps`;
  return `${Math.round(kbps)} kbps`;
}

/** Format ms with one decimal under 1 s, integer seconds above. */
function formatLatency(ms?: number): string | null {
  if (typeof ms !== 'number' || !Number.isFinite(ms)) return null;
  if (ms >= 1000) return `${(ms / 1000).toFixed(2)} s`;
  return `${ms.toFixed(1)} ms`;
}

function formatBandList(fdd?: number[], tdd?: number[]): string | null {
  const parts: string[] = [];
  if (fdd && fdd.length > 0) parts.push(...fdd.map(b => `B${b}`));
  if (tdd && tdd.length > 0) parts.push(...tdd.map(b => `B${b}`));
  if (parts.length === 0) return null;
  // Cap at 8 visible so we don't blow up the tile; tooltip shows the full list.
  if (parts.length <= 8) return parts.join(' · ');
  return `${parts.slice(0, 8).join(' · ')} · +${parts.length - 8}`;
}

function formatNrBandList(sa?: number[], nsa?: number[]): string | null {
  const labels: string[] = [];
  if (sa && sa.length > 0) labels.push(...sa.map(b => `N${b}`));
  if (nsa && nsa.length > 0) labels.push(...nsa.map(b => `N${b}`));
  if (labels.length === 0) return null;
  const dedup = Array.from(new Set(labels));
  if (dedup.length <= 6) return dedup.join(' · ');
  return `${dedup.slice(0, 6).join(' · ')} · +${dedup.length - 6}`;
}

function isHomeRegistration(state?: number): boolean | undefined {
  if (state === undefined) return undefined;
  if (state === 1) return true; // home
  if (state === 5) return false; // roaming
  return undefined;
}

function plmnString(mcc?: string, mnc?: string): string | undefined {
  if (!mcc) return undefined;
  if (!mnc) return mcc;
  return `${mcc}/${mnc}`;
}

/**
 * 4-bar signal indicator from RSRP. Mapping mirrors what cellular modems
 * commonly report on phone status bars:
 *   RSRP ≥ -85 dBm  → 4 bars
 *   ≥ -95            → 3
 *   ≥ -105           → 2
 *   ≥ -115           → 1
 *   < -115 / unknown → 0
 * Falls back to RSSI bucketing when RSRP is missing (2G/3G).
 */
function signalBars(rsrp?: number, rssi?: number): number {
  if (typeof rsrp === 'number' && !Number.isNaN(rsrp)) {
    if (rsrp >= -85) return 4;
    if (rsrp >= -95) return 3;
    if (rsrp >= -105) return 2;
    if (rsrp >= -115) return 1;
    return 0;
  }
  if (typeof rssi === 'number' && !Number.isNaN(rssi)) {
    if (rssi >= -65) return 4;
    if (rssi >= -75) return 3;
    if (rssi >= -85) return 2;
    if (rssi >= -95) return 1;
    return 0;
  }
  return 0;
}

/**
 * 3GPP Access Technology codes (TS 27.007 §7.3, +CGREG/+CEREG `AcT` field).
 * Modems often report these as numbers in `network_status.network_mode` /
 * `networkType`. We translate them to human labels so the UI never shows
 * raw "7" or "9" to a customer.
 */
const ACT_CODE_LABELS: Record<number, string> = {
  0: 'GSM',
  1: 'GSM Compact',
  2: 'UMTS',
  3: 'GSM/EGPRS',
  4: 'HSDPA',
  5: 'HSUPA',
  6: 'HSPA+',
  7: 'LTE',
  8: 'LTE-M',          // EC-GSM-IoT in spec, but most Quectel firmwares co-opt 8 for LTE-M
  9: 'NB-IoT',         // E-UTRAN NB-S1 mode
  10: 'E-UTRAN NB-S1',
  11: '5G NSA',
  12: '5G SA',
};

/**
 * Map any "technology" value (string from CLAUDE.md primitive specs, number
 * from 3GPP AcT codes, or numeric string from older edge payloads) to a
 * stable display label. Returns `null` if we can't make sense of it — the
 * caller hides the row in that case.
 */
function formatTechnology(raw: unknown): string | null {
  if (raw === null || raw === undefined || raw === '') return null;
  // Numeric AcT codes (or numeric strings).
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return ACT_CODE_LABELS[raw] ?? null;
  }
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return null;
    const asNum = Number(trimmed);
    if (Number.isInteger(asNum) && asNum >= 0 && asNum <= 99) {
      const mapped = ACT_CODE_LABELS[asNum];
      if (mapped) return mapped;
    }
    // Already a string label like "LTE", "FDD LTE", "NR5G-NSA", etc. Pass through.
    return trimmed;
  }
  return null;
}

function ratBadgeColor(tech?: unknown): string {
  if (typeof tech !== 'string' || !tech) return 'bg-muted text-muted-foreground border-border';
  const t = tech.toUpperCase();
  if (t.includes('NR') || t === '5G' || t === 'VONR') {
    return 'bg-purple-500/10 text-purple-700 dark:text-purple-300 border-purple-500/20';
  }
  if (t.includes('LTE-M') || t === 'LTE_M' || t === 'CAT-M') {
    return 'bg-cyan-500/10 text-cyan-700 dark:text-cyan-300 border-cyan-500/20';
  }
  if (t.includes('NB-IOT') || t === 'NBIOT' || t === 'NB_IOT') {
    return 'bg-teal-500/10 text-teal-700 dark:text-teal-300 border-teal-500/20';
  }
  if (t.includes('LTE') || t === 'VOLTE' || t === '4G') {
    return 'bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/20';
  }
  if (t.includes('UMTS') || t === '3G' || t.includes('WCDMA') || t === 'HSPA') {
    return 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/20';
  }
  if (t.includes('GSM') || t === '2G' || t === 'EDGE') {
    return 'bg-orange-500/10 text-orange-700 dark:text-orange-300 border-orange-500/20';
  }
  return 'bg-muted text-muted-foreground border-border';
}

function SignalBars({ bars, total = 4 }: { bars: number; total?: number }): React.ReactElement {
  return (
    <div className="flex items-end gap-0.5 h-4">
      {Array.from({ length: total }).map((_, i) => {
        const filled = i < bars;
        const heightPct = ((i + 1) / total) * 100;
        return (
          <div
            key={i}
            className={cn(
              'w-1 rounded-sm transition-colors',
              filled ? 'bg-foreground' : 'bg-muted-foreground/30'
            )}
            style={{ height: `${heightPct}%` }}
          />
        );
      })}
    </div>
  );
}

export function ConnectivitySnapshot(props: ConnectivitySnapshotProps): React.ReactElement | null {
  const s = normalize(props);
  if (!hasAnyData(s)) return null;

  const isHome = isHomeRegistration(s.registrationState);
  const plmn = plmnString(s.operatorMcc, s.operatorMnc);
  const bars = signalBars(s.signalRsrp, s.signalRssi);

  const requestedDescribed =
    s.requestedMcc || s.requestedRat
      ? [
          plmnString(s.requestedMcc, s.requestedMnc),
          typeof s.requestedRat === 'string' && s.requestedRat && s.requestedRat !== 'auto'
            ? s.requestedRat.toUpperCase()
            : undefined,
        ]
          .filter(Boolean)
          .join(' / ')
      : null;

  return (
    <TooltipProvider delayDuration={150}>
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-baseline justify-between mb-4">
          <div>
            <h3 className="text-base font-semibold text-foreground">Connectivity snapshot</h3>
            <p className="text-xs text-muted-foreground">What the modem actually got on this run</p>
          </div>
          {requestedDescribed && (
            <div className="flex items-center gap-1.5 text-xs">
              <span className="text-muted-foreground">Requested: {requestedDescribed}</span>
              {s.requestedHonored === true && (
                <Badge
                  variant="outline"
                  className="bg-status-success-bg text-status-success border-status-success/20 px-1.5 py-0 h-5"
                >
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                  honored
                </Badge>
              )}
              {s.requestedHonored === false && (
                <Badge
                  variant="outline"
                  className="bg-status-warning-bg text-status-warning border-status-warning/20 px-1.5 py-0 h-5"
                >
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  fell back
                </Badge>
              )}
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {/* Country / Location — where did this run actually happen? */}
          {(s.countryName || s.locationName) && (
            <div className="rounded-lg border bg-muted/30 p-3">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                <MapPin className="h-3 w-3" />
                Where
              </div>
              {s.countryName && (
                <p className="text-base font-semibold text-foreground break-words leading-tight" title={s.countryName}>
                  {s.countryName}
                </p>
              )}
              {s.locationName && (
                <p className="text-xs text-muted-foreground mt-1 break-words" title={s.locationName}>
                  {s.locationName}
                </p>
              )}
            </div>
          )}

          {/* Serving network — the PLMN the modem actually attached to.
              Headline = alpha operator name when known, otherwise the
              numeric PLMN. Sub-line always carries MCC/MNC explicitly
              when known, so the customer can verify "I'm on 425/01 in
              Israel" even before the alpha name resolves. */}
          {(s.operatorName || plmn || s.operatorMcc) && (
            <div className="rounded-lg border bg-muted/30 p-3">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                <Globe className="h-3 w-3" />
                Network
              </div>
              <p
                className="text-base font-semibold text-foreground break-words leading-tight"
                title={s.operatorName || plmn || `MCC ${s.operatorMcc}`}
              >
                {s.operatorName || plmn || `MCC ${s.operatorMcc}`}
              </p>
              <div className="flex items-center gap-1.5 mt-1 text-xs text-muted-foreground flex-wrap">
                {plmn ? (
                  <span className="font-mono">PLMN {plmn}</span>
                ) : s.operatorMcc ? (
                  <span className="font-mono">MCC {s.operatorMcc}</span>
                ) : null}
                {!s.operatorName && (plmn || s.operatorMcc) && (
                  <span className="italic">(operator name pending)</span>
                )}
                {isHome === true && (
                  <Badge
                    variant="outline"
                    className="px-1.5 py-0 h-4 bg-status-success-bg text-status-success border-status-success/20"
                  >
                    Home
                  </Badge>
                )}
                {isHome === false && (
                  <Badge
                    variant="outline"
                    className="px-1.5 py-0 h-4 bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/20"
                  >
                    Roaming
                  </Badge>
                )}
              </div>
            </div>
          )}

          {/* RAT / Technology — shows the high-level label (LTE / NR) +
              the more specific access-tech string from QNWINFO when
              available (e.g. "FDD LTE", "NR5G-NSA"). NSA badges (Phase 3)
              surface "5G NSA" / "5G NSA (aggregating)" / "EN-DC restricted"
              based on classifyNsaState — same tiers a phone's icon uses. */}
          {(s.technology || s.bearerClass || s.accessTechName || s.nsaState) && (
            <div className="rounded-lg border bg-muted/30 p-3">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                <Wifi className="h-3 w-3" />
                Technology
              </div>
              {s.technology && (
                <Badge
                  variant="outline"
                  className={cn('px-2 py-0.5 text-sm font-semibold', ratBadgeColor(s.technology))}
                >
                  {s.technology}
                </Badge>
              )}
              {s.nsaState === 'nsa_aggregating' && (
                <Badge
                  variant="outline"
                  className="ml-1 px-2 py-0.5 text-sm font-semibold bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/30"
                  title="QENDC + QCAINFO confirm NR is aggregating with LTE — phone equivalent: 5G+ / 5G UC"
                >
                  5G NSA+
                </Badge>
              )}
              {s.nsaState === 'nsa_available' && (
                <Badge
                  variant="outline"
                  className="ml-1 px-2 py-0.5 text-sm font-semibold bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/30"
                  title="QENDC field 4 = 1: cell supports NSA and UE is admitted. Phone equivalent: plain 5G icon"
                >
                  5G NSA
                </Badge>
              )}
              {s.nsaState === 'endc_capable_no_attach' && (
                <Badge
                  variant="outline"
                  className="ml-1 px-2 py-0.5 text-xs font-semibold bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30"
                  title={
                    s.endcRestrict === 1
                      ? 'Cell + PLMN broadcast EN-DC support but the network has gated this UE off NSA (RestrictDCNR).'
                      : 'Cell or PLMN supports EN-DC but UE is not currently admitted.'
                  }
                >
                  EN-DC capable
                </Badge>
              )}
              {s.accessTechName && s.accessTechName !== s.technology && (
                <p className="text-xs text-muted-foreground mt-1.5">{s.accessTechName}</p>
              )}
              {s.bearerClass && (
                <p className="text-xs text-muted-foreground mt-0.5">Bearer: {s.bearerClass}</p>
              )}
            </div>
          )}

          {/* NR secondary cell (Phase 3 of 5G NSA). Renders only when the
              modem reports an aggregating NR SCell (via AT+QCAINFO NR SCC
              or AT+QENG's "NR5G-NSA" line). LTE PCell stays as the anchor
              in the "Band (live)" tile above. */}
          {(s.nrBand !== undefined || s.nrArfcn !== undefined || s.nrRsrp !== undefined) && (
            <div className="rounded-lg border bg-muted/30 p-3">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                <Radio className="h-3 w-3" />
                NR SCell (5G)
              </div>
              {s.nrBand !== undefined && (
                <p className="text-base font-semibold text-foreground break-words leading-tight">
                  N{s.nrBand}
                </p>
              )}
              <div className="text-xs text-muted-foreground space-y-0.5 mt-1">
                {s.nrArfcn !== undefined && <p>ARFCN {s.nrArfcn}</p>}
                {s.nrPci !== undefined && <p>PCI {s.nrPci}</p>}
                {s.nrRsrp !== undefined && (
                  <p>
                    RSRP {s.nrRsrp} dBm
                    {s.nrSinr !== undefined && ` · SINR ${s.nrSinr} dB`}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Live serving-cell band, bandwidth, and cell ID — populated
              by the edge's QNWINFO + QENG periodic poll (v4.1.0+). Shows
              "what band did I attach to, and how wide is that channel?".
              Falls back to the "Modem capability" tile below when this
              isn't available yet. */}
          {(s.bandLive || s.dlBandwidth) && (
            <div className="rounded-lg border bg-muted/30 p-3">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                <Radio className="h-3 w-3" />
                Band (live)
              </div>
              {s.bandLive && (
                <p
                  className="text-base font-semibold text-foreground break-words leading-tight"
                  title={s.bandLive}
                >
                  {s.bandLive}
                </p>
              )}
              {s.dlBandwidth && (
                <p className="text-xs text-foreground mt-1">
                  {s.dlBandwidth}
                  {s.ulBandwidth && s.ulBandwidth !== s.dlBandwidth ? ` ↓ · ${s.ulBandwidth} ↑` : ''}
                  <span className="text-muted-foreground"> channel</span>
                </p>
              )}
              <div className="text-xs text-muted-foreground mt-1 space-x-2">
                {typeof s.bandChannel === 'number' && <span>EARFCN {s.bandChannel}</span>}
                {typeof s.pci === 'number' && <span>PCI {s.pci}</span>}
              </div>
              {s.cellId && (
                <p
                  className="text-xs text-muted-foreground mt-0.5 font-mono break-all"
                  title={`Cell ID 0x${s.cellId}`}
                >
                  Cell 0x{s.cellId}
                </p>
              )}
            </div>
          )}

          {/* Modem capability — what the modem HARDWARE supports. Useful
              as a baseline answer when the live band isn't being polled
              yet. Renders as soon as we resolved the modem model. */}
          {(s.lteCategoryMax ||
            (s.lteFddBands && s.lteFddBands.length > 0) ||
            (s.lteTddBands && s.lteTddBands.length > 0) ||
            (s.nrSaBands && s.nrSaBands.length > 0) ||
            (s.nrNsaBands && s.nrNsaBands.length > 0)) && (
            <div className="rounded-lg border bg-muted/30 p-3">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                <Cpu className="h-3 w-3" />
                Modem capability
              </div>
              {s.lteCategoryMax && (
                <p className="text-base font-semibold text-foreground">
                  Cat {s.lteCategoryMax}{' '}
                  <span className="text-xs text-muted-foreground font-normal">(LTE max)</span>
                </p>
              )}
              {(s.lteFddBands || s.lteTddBands) && (() => {
                const list = formatBandList(s.lteFddBands, s.lteTddBands);
                if (!list) return null;
                const fullList = [
                  ...(s.lteFddBands ?? []).map(b => `B${b}`),
                  ...(s.lteTddBands ?? []).map(b => `B${b}`),
                ].join(' · ');
                // `list` is summarised ("…+20"); full list lives behind a hover.
                const summarised = `LTE ${list}` !== `LTE ${fullList}`;
                const body = (
                  <p
                    className={cn(
                      'text-xs text-muted-foreground mt-1 break-words',
                      summarised && 'cursor-help underline decoration-dotted decoration-muted-foreground/40 underline-offset-2',
                    )}
                  >
                    LTE {list}
                  </p>
                );
                if (!summarised) return body;
                return (
                  <Tooltip>
                    <TooltipTrigger asChild>{body}</TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <span className="text-xs">LTE bands: {fullList}</span>
                    </TooltipContent>
                  </Tooltip>
                );
              })()}
              {(() => {
                const nrList = formatNrBandList(s.nrSaBands, s.nrNsaBands);
                if (!nrList) return null;
                return (
                  <p className="text-xs text-muted-foreground mt-0.5 break-words">
                    NR {nrList}
                  </p>
                );
              })()}
            </div>
          )}

          {/* Signal */}
          {(s.signalRsrp !== undefined || s.signalRssi !== undefined) && (
            <div className="rounded-lg border bg-muted/30 p-3">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                <Signal className="h-3 w-3" />
                Signal
              </div>
              <div className="flex items-center gap-2">
                <SignalBars bars={bars} />
                <p className="text-base font-semibold text-foreground">
                  {s.signalRsrp !== undefined
                    ? `${s.signalRsrp} dBm`
                    : `${s.signalRssi} dBm`}
                </p>
              </div>
              <div className="text-xs text-muted-foreground mt-1 space-x-2">
                {s.signalRsrp !== undefined && <span>RSRP {s.signalRsrp}</span>}
                {s.signalRsrq !== undefined && <span>RSRQ {s.signalRsrq}</span>}
                {s.signalSinr !== undefined && <span>SINR {s.signalSinr}</span>}
              </div>
            </div>
          )}

          {/* Latency — headline RTT from the data test. Min/max shown as
              sub-line so the user gets jitter at a glance. */}
          {s.latencyAvgMs !== undefined && (
            <div className="rounded-lg border bg-muted/30 p-3">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                <Timer className="h-3 w-3" />
                Latency
              </div>
              <p className="text-base font-semibold text-foreground">
                {formatLatency(s.latencyAvgMs)}
                <span className="text-xs text-muted-foreground font-normal ml-1">avg</span>
              </p>
              {(s.latencyMinMs !== undefined || s.latencyMaxMs !== undefined) && (
                <p className="text-xs text-muted-foreground mt-1">
                  {s.latencyMinMs !== undefined && <>min {formatLatency(s.latencyMinMs)}</>}
                  {s.latencyMinMs !== undefined && s.latencyMaxMs !== undefined && ' · '}
                  {s.latencyMaxMs !== undefined && <>max {formatLatency(s.latencyMaxMs)}</>}
                </p>
              )}
              {typeof s.packetLossPct === 'number' && s.packetLossPct > 0 && (
                <p className="text-xs text-status-warning mt-0.5">
                  {s.packetLossPct.toFixed(1)}% loss
                </p>
              )}
            </div>
          )}

          {/* Throughput — headline downlink speed when an HTTP / download
              test ran. DNS shown as sub-line for context. */}
          {s.throughputKbps !== undefined && (
            <div className="rounded-lg border bg-muted/30 p-3">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                <Gauge className="h-3 w-3" />
                Throughput
              </div>
              <p className="text-base font-semibold text-foreground">
                {formatThroughput(s.throughputKbps)}
              </p>
              {typeof s.dnsMs === 'number' && (
                <p className="text-xs text-muted-foreground mt-1">
                  DNS {formatLatency(s.dnsMs)}
                </p>
              )}
            </div>
          )}

          {/* IP */}
          {s.ip && (
            <div className="rounded-lg border bg-muted/30 p-3">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                <MapPin className="h-3 w-3" />
                IP
              </div>
              <p
                className="text-sm font-mono font-semibold text-foreground break-all"
                title={s.ip}
              >
                {s.ip}
              </p>
              {typeof s.ipFamily === 'string' && s.ipFamily && (
                <p className="text-xs text-muted-foreground mt-1">{s.ipFamily.toUpperCase()}</p>
              )}
            </div>
          )}

          {/* APN */}
          {s.apn && (
            <div className="rounded-lg border bg-muted/30 p-3">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                APN
              </div>
              <p
                className="text-sm font-mono font-semibold text-foreground break-all"
                title={s.apn}
              >
                {s.apn}
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
    </TooltipProvider>
  );
}
